/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { SimpleTooltip } from '../../simple-tooltip';
import { AssuranceModal } from '../../assurance/assurance-modal';
import { loadBlueprintMapping } from '../../../actions/blueprints';
import { setOverviewMode } from '../../../slice/surveySettings';
import SurveyQuestionList from '../../survey-question-list/survey-question-list';
import { getSurveyGroupsSelector } from '../../../selectors/survey';
import { reloadSurveyListSummary } from '../../../actions/survey';
import { getCurrentUser, isUserManagerByInitiativeId } from '../../../selectors/user';
import { SurveyContext, SurveyContextLoadedProps } from '../../survey-container/SurveyContainer';
import LoadingPlaceholder from '../../LoaderContainer/LoadingPlaceholder';
import { ROUTES } from '../../../constants/routes'
import { generateDisabledUtrs, hasUTRVFilter } from '../utils/getDisableUtrs'
import { DashboardSection, DashboardSectionProps } from '../../dashboard';
import { getSurveyFlexSearchMap } from '../../../selectors/blueprint';
import { RootState } from '../../../reducers';
import { SurveyActionData } from '../../../model/surveyData';
import { getInitiativeMateriality, isRootOrg } from '../../../selectors/initiative';
import QuestionProgressContainer from './question-progress-container/index';
import { generateUrl } from '../../../routes/util';
import { getSurveyOverviewMode } from '../../../selectors/appConfig';
import { SurveySettings, SurveyType } from '../../../types/survey';
import { SurveyMessageModal } from '../../message-modal/MessageModal';
import { BulkActionToolbar } from '../../survey-question-list/partials/BulkActionToolbar';
import { Divider } from '../../tracking-list';
import { ColumnCode } from '../../survey-question-list/partials/ColumnCode';
import { ColumnTitle } from '../../survey-question-list/partials/ColumnTitle';
import { ColumnFlags } from '../../survey-question-list/partials/ColumnFlags';
import { ColumnValue } from '../../survey-question-list/partials/ColumnValue';
import { ColumnStatusIcon } from '../../survey-question-list/partials/ColumnStatusIcon';
import { ColumnOptionsMenu } from '../../survey-question-list/partials/ColumnOptionsMenu';
import { getSurveyName } from '../../../utils/surveyData';
import { SurveyQuestionListToolbar } from '../survey-question-list-toolbar';
import { getInitialSurveySettings, isAutoAggregatedSurvey } from '../../../utils/survey';
import { Spacer } from '../survey-question-list-toolbar/partials/Spacer';
import { FilterQuestionsToggle } from '../survey-question-list-toolbar/partials/FilterQuestionsToggle';
import { SearchQuestions } from '../survey-question-list-toolbar/partials/SearchQuestions';
import { ScopeViewDropdown } from '../survey-question-list-toolbar/partials/ScopeViewDropdown';
import { ExpandAllToggle } from '../survey-question-list-toolbar/partials/ExpandAllToggle';
import { OutdatedSurveyAlert } from './OutdatedSurveyAlert';
import { ShowingBookmarksToggle } from '../survey-question-list-toolbar/partials/ShowingBookmarksToggle';
import './overview.scss';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { endpoints } from '../../../api/metric-groups';
import { endpoints as bookmarksEndpoints } from '../../../api/bookmarks';
import { SurveyPermissions } from '../../../services/permissions/SurveyPermissions';
import { QUESTION } from '../../../constants/terminology';
import { ColumnBookmark } from '../../survey-question-list/partials/ColumnBookmark';

type Props = PropsFromRedux & RouteComponentProps;

interface AssuranceModalState {
  isOpen: boolean;
  surveyId?: string;
  initiativeId?: string
}

interface State {
  questionCount: number;
  messageModal: { isOpen: boolean, surveyId: string | undefined };
  assuranceModal: AssuranceModalState;
  disabledUTRs: undefined | string[];
  sidebarSettings: SurveySettings;
  surveyUpdated: boolean;
  expandedQuestionGroupId: string | undefined;
}

class SurveyOverview extends React.Component<Props> {

  static contextType = SurveyContext;

  state: State = {
    questionCount: 0,
    messageModal: {
      isOpen: false,
      surveyId: undefined
    },
    assuranceModal: {
      isOpen: false,
      surveyId: undefined,
      initiativeId: undefined,
    },
    disabledUTRs: undefined,
    sidebarSettings: getInitialSurveySettings(),
    surveyUpdated: false,
    expandedQuestionGroupId: undefined,
  };

  componentDidMount() {
    this.updateSurveyState();
    this.updateDisabledUTRs();
    this.presetOverviewMode();

    const { surveyData } = this.context as SurveyContextLoadedProps;
    this.props.getBookmarks({surveyId: surveyData._id});
  }

  componentDidUpdate(prevProps: Props) {
    const { sidebarSettings } = this.context as SurveyContextLoadedProps;

    this.updateSurveyState();

    const watchedProps = Object.keys(this.state.sidebarSettings) as (keyof SurveySettings)[];
    if (watchedProps.some(prop => sidebarSettings[prop] !== this.state.sidebarSettings[prop])) {
      this.setState({ sidebarSettings: sidebarSettings }, () => this.updateDisabledUTRs());
    }

    const reduxProps: (keyof Props)[] = ['surveyGroups', 'blueprintState', 'materiality', 'searchIndex']
    if (reduxProps.some(prop => this.props[prop] !== prevProps[prop])) {
      this.updateDisabledUTRs()
    }
  }

  updateSurveyState = () => {
    const { isLoaded, surveyData } = this.context as SurveyContextLoadedProps;
    if (!isLoaded || this.state.surveyUpdated) {
      return;
    }

    this.setState({ surveyUpdated: true }, () => {
      this.props.loadBlueprintMapping(surveyData.sourceName);
    });
  }

  presetOverviewMode = () => {
    if (this.props.surveyOverviewMode !== this.props.defaultSurveyOverviewMode) {
      // Reset to default mode on mount;
      this.props.setOverviewMode(this.props.defaultSurveyOverviewMode);
    }
  }

  handleAddAssurance = (survey: SurveyActionData) => {
    this.setState({
      assuranceModal: {
        ...this.state.assuranceModal,
        isOpen: true,
        surveyId: survey._id,
        initiativeId: survey.initiativeId,
      },
    });
  }

  toggleMessageModal = (surveyId?: string) => {
    this.setState({
      messageModal: {
        isOpen: !surveyId ? false : !this.state.messageModal.isOpen,
        surveyId: surveyId
      }
    });
  }

  toggleAssurance = (submitted?: boolean) => {
    if (!this.props.canAccessAssurance) {
      return;
    }

    this.setState({
      assuranceModal: {
        ...this.state.assuranceModal,
        isOpen: !this.state.assuranceModal.isOpen,
      },
    }, () => {
      if (submitted === true) {
        this.props.reloadSurveyListSummary();
      }
    });
  };

  updateDisabledUTRs = () => {
    const { surveyGroups, blueprintState, materiality, searchIndex, user, tags, bookmarks } = this.props;
    const { sidebarSettings, questions } = this.context as SurveyContextLoadedProps;

    const {
      filterByGoal,
      searchText,
      filterByStatus,
      filterByStandard,
      filterByFramework,
      filterByRegulatory,
      filterByMateriality,
      filterByDelegationStatus,
      filterByTag,
      filterByBookmarks
    } = sidebarSettings;

    if (!hasUTRVFilter(sidebarSettings)) {
      return this.setState({ disabledUTRs: undefined, questionCount: questions.length });
    }

    const disabledUTRs = generateDisabledUtrs({
      surveyGroups,
      blueprint: blueprintState.data,
      materiality,
      filterByGoal,
      searchText,
      filterByStatus,
      filterByStandard,
      filterByFramework,
      filterByRegulatory,
      filterByMateriality,
      filterByDelegationStatus,
      filterByTag,
      filterByBookmarks,
      searchIndex,
      user,
      tags: tags.data,
      bookmarks: bookmarks.data,
    })
    const ids = this.getQuestionIds(disabledUTRs);
    this.setState({ disabledUTRs, questionCount: ids.length });
  }

  redirectToPage(initiativeId: string, surveyId: string, page: string) {
    return this.props.history.push({
      pathname: generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page }),
      search: this.props.history.location.search,
    });
  }

  onClickScopeWheel = (groupId: string) => {
    this.setState({ expandedQuestionGroupId: groupId });
  }

  resetExpandedQuestionGroup = () => {
    this.setState({ expandedQuestionGroupId: undefined });
  }

  render() {
    const {
      surveyData,
      isLoaded: contextLoaded,
      isReadOnly,
      sidebarSettings,
      handleChangeSettings
    } = this.context as SurveyContextLoadedProps;
    const { blueprintState, surveyOverviewMode, isManager, canAccessAssurance, surveyGroups, canManage, isRootOrganization } = this.props;

    const isLoaded = contextLoaded && blueprintState.loaded;

    const surveyScopeLink = contextLoaded
      ? generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
        initiativeId: surveyData.initiativeId,
        surveyId: surveyData._id,
        page: 'scope',
      })
      : '';

    const noRowsMessage = hasUTRVFilter(sidebarSettings) ? (
      <>
        You have no {QUESTION.PLURAL} in scope matching your search. If you are an Admin you can add additional survey scope{' '}
        <Link to={surveyScopeLink}>
          <u>here</u>
        </Link>
        .
      </>
    ) : this.props.isManager ? (
      <>
        You have no {QUESTION.PLURAL} in scope. Please update your survey scope{' '}
        <Link to={surveyScopeLink}>
          <u>here</u>
        </Link>
        .
      </>
    ) : (
      <>
        There are no {QUESTION.PLURAL} to answer in this survey. Please contact your survey admin if you think there should be
        {QUESTION.PLURAL} assigned to you.
      </>
    );

    const isCombinedReport = surveyData.type === SurveyType.Aggregation;
    const isAutoCombinedReport = isAutoAggregatedSurvey(surveyData.type);
    const OverviewDropdown = () => {
      if (!isManager || isAutoCombinedReport) {
        return null;
      }

      return (
        <UncontrolledDropdown>
          <DropdownToggle color='transparent' caret>
            <i className='fas fa-bars mr-2' />Survey settings
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem onClick={() => this.redirectToPage(surveyData.initiativeId, surveyData._id, 'configuration')}>
              <i className='fal fa-cog mr-2' />Configuration & Scope
            </DropdownItem>
            <DropdownItem onClick={() => this.toggleMessageModal(surveyData._id)}>
              <i className='fal fa-envelope mr-2' />Message Users
            </DropdownItem>
            {!isCombinedReport ? <DropdownItem onClick={() => this.redirectToPage(surveyData.initiativeId, surveyData._id, 'import-export')}>
              <i className='fal fa-file-import mr-2' />Import survey data
            </DropdownItem> : null}
            {!isCombinedReport ? (
              <><DropdownItem divider />
                {!canAccessAssurance ?
                  <SimpleTooltip text={'Upgrade to Company Tracker Pro to access this feature'}>
                    <DropdownItem disabled={true}><i className='fa fa-award mr-2' />Manage assurance</DropdownItem>
                  </SimpleTooltip>
                  : <DropdownItem onClick={() => this.handleAddAssurance(surveyData)}>
                    <i className='fa fa-award mr-2' />Manage assurance
                  </DropdownItem>
                }</>
            ) : null}
          </DropdownMenu>
        </UncontrolledDropdown>
      );
    }

    const isSurveyComplete = Boolean(surveyData?.completedDate);

    const sectionProps: DashboardSectionProps = isAutoCombinedReport
      ? { title: 'Aggregated survey', headingStyle: 4 }
      : {
          icon: 'fa fa-list',
          title: `Survey overview: ${getSurveyName(surveyData)}`,
          buttons: [<OverviewDropdown />],
        };

    return (
      <>
        <OutdatedSurveyAlert initiativeId={surveyData.initiativeId} surveyData={surveyData} />
        <DashboardSection {...sectionProps} className='overviewContainer'>
          <div>
            <LoadingPlaceholder className='mt-0 mb-1' count={1} height={39} isLoading={!isLoaded}>
              <QuestionProgressContainer
                surveyGroups={this.props.surveyGroups}
                config={surveyData.scopeConfig}
                onClickScopeWheel={this.onClickScopeWheel}
                readOnly={isCombinedReport || isAutoCombinedReport}
              />
            </LoadingPlaceholder>

            <div className='mt-1 question-list__wrapper'>
              <LoadingPlaceholder className='mt-0 mb-1' count={3} height={39} isLoading={!isLoaded}>
                <SurveyQuestionList
                  key={surveyOverviewMode}
                  expandedQuestionGroupId={this.state.expandedQuestionGroupId}
                  resetExpandedQuestionGroup={this.resetExpandedQuestionGroup}
                  surveyId={surveyData._id}
                  initiativeId={surveyData.initiativeId}
                  surveyGroups={surveyGroups}
                  disabledUTRs={this.state.disabledUTRs}
                  noRowsMessage={noRowsMessage}
                  allowBulkActions={!isReadOnly}
                  bulkActionToolbar={(props) => (
                    <>
                      {isReadOnly ? null : (
                        <BulkActionToolbar
                          {...props}
                          surveyData={surveyData}
                          hiddenOptions={canManage && isRootOrganization ? ['decimal'] : ['decimal', 'tag']}
                        />
                      )}
                    </>
                  )}
                  columns={[
                    ColumnBookmark,
                    ColumnCode,
                    Divider,
                    ColumnTitle,
                    (props) => <ColumnFlags {...props}
                      verificationRequired={surveyData.verificationRequired}
                      evidenceRequired={surveyData.evidenceRequired}
                      noteRequired={Boolean(surveyData.noteRequired)}
                    />,
                    Divider,
                    ColumnValue,
                    Divider,
                    ColumnStatusIcon,
                    (props) => <ColumnOptionsMenu {...props} survey={surveyData} />,
                  ]}
                  toolbar={(props) => (
                    <SurveyQuestionListToolbar
                      {...props}
                      settings={sidebarSettings}
                      handleChangeSettings={handleChangeSettings}
                      surveyScope={surveyData.scope}
                      isShowingBookmarks={sidebarSettings.filterByBookmarks}
                      toggleShowingBookmarks={(hasFilterByBookmarks: boolean) => handleChangeSettings('filterByBookmarks', hasFilterByBookmarks)}
                      components={[
                        ScopeViewDropdown,
                        SearchQuestions,
                        FilterQuestionsToggle,
                        Spacer,
                        ShowingBookmarksToggle,
                        ExpandAllToggle,
                      ]}
                    />
                  )}
                />
              </LoadingPlaceholder>
            </div>

            {isManager && !isSurveyComplete && !(isCombinedReport || isAutoCombinedReport) ?
              <div className='add-survey-pack'>
                <Button outline className='add-survey-pack__button' onClick={() => this.redirectToPage(surveyData.initiativeId, surveyData._id, 'scope')}>
                  <i className='fa-solid fa-plus mr-2' />
                  Add survey pack
                </Button>
              </div>
              : null}
          </div>

          {this.renderAssuranceModal()}
          {this.renderMessageModal()}
        </DashboardSection>
      </>
    );
  }

  private renderAssuranceModal() {
    const { isOpen, surveyId, initiativeId } = this.state.assuranceModal;
    if (!isOpen || !surveyId || !initiativeId) {
      return <></>
    }

    return <AssuranceModal
      toggle={this.toggleAssurance}
      isOpen={isOpen}
      surveyId={surveyId}
      initiativeId={initiativeId}
    />;
  }

  private renderMessageModal() {
    const { isOpen, surveyId } = this.state.messageModal;
    if (!isOpen || !surveyId) {
      return null;
    }

    const { surveyData } = this.context as SurveyContextLoadedProps;
    return (
      <SurveyMessageModal toggle={() => this.toggleMessageModal()} subject={getSurveyName(surveyData)} surveyId={surveyId} />
    );
  }

  private getQuestionIds(disabledUTRs: string[]) {
    const disabled = new Set(disabledUTRs);
    const ids = new Set<string>();
    const surveyGroups = this.props.surveyGroups;
    surveyGroups.forEach(group => {
      if (Array.isArray(group.list)) {
        group.list.forEach(q => {
          const id = q.utrv?._id;
          if (id && !disabled.has(q.universalTracker.getId())) {
            ids.add(id)
          }
        })
      }
    });
    return Array.from(ids);
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    user: getCurrentUser(state),
    materiality: getInitiativeMateriality(state),
    defaultSurveyOverviewMode: getSurveyOverviewMode(state),
    surveyOverviewMode: state.surveySettings.overviewMode,
    surveyGroups: getSurveyGroupsSelector(state),
    blueprintState: state.blueprint,
    isManager: isUserManagerByInitiativeId(state, state.survey.loaded ? state.survey.data.initiativeId : undefined),
    canManage: state.survey.loaded && state.currentUser.loaded && SurveyPermissions.canManage(state.survey.data, state.currentUser.data),
    searchIndex: getSurveyFlexSearchMap(state),
    isRootOrganization: isRootOrg(state),
    canAccessAssurance: FeaturePermissions.canAccessAssurance(state),
    tags: endpoints.getCustomTags.select(state.survey.loaded ? state.survey.data.initiativeId : '')(state),
    bookmarks: bookmarksEndpoints.getUtrvBookmarks.select({surveyId: state.survey.loaded ? state.survey.data._id : ''})(state),
  };
};

const mapDispatchToProps = {
  loadBlueprintMapping,
  reloadSurveyListSummary,
  setOverviewMode,
  getBookmarks: bookmarksEndpoints.getUtrvBookmarks.initiate,
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

export default withRouter(connector(SurveyOverview));
